import React from 'react';
import { Alert, Box, Button, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import NaturalCaptureComposerShell from '../../components/UI/NaturalCaptureComposerShell';
import { configureAppointment } from '../../services/appointmentScheduleService';
import { getAppointmentLeadMinutes, getAppointmentReminderMinutes, getZonedDayKey } from './appointmentPresentation';

const FORM_ID = 'appointment-review-form';
const getInitialMode = (appointment) => {
  if (appointment?.id) return 'edit';
  if (appointment?.sourceRef) return 'review';
  return 'manual';
};

const AppointmentEditorSheet = ({ child, timeZone, appointment = null, onClose }) => {
  const childId = child?.id || appointment?.childId || '';
  const [mode] = React.useState(getInitialMode(appointment));
  const [statement] = React.useState(appointment?.originalStatement || '');
  const [clarification, setClarification] = React.useState(appointment?.clarification || '');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [values, setValues] = React.useState({
    title: appointment?.title || '',
    provider: appointment?.provider || '',
    date: appointment?.startDate || appointment?.date || '',
    time: appointment?.startTime || appointment?.time || '',
    enabled: Boolean(appointment ? appointment.reminderRules?.length : true),
  });

  const leadMinutes = values.date && values.time
    ? getAppointmentLeadMinutes({ startDate: values.date, startTime: values.time, timeZone })
    : null;
  const reminderMinutes = getAppointmentReminderMinutes(
    { startDate: values.date, startTime: values.time, timeZone }, values.enabled,
  );
  const reminderUnavailable = values.enabled && leadMinutes !== null && leadMinutes < 60;
  const appointmentIsFuture = Boolean(values.date && values.time && leadMinutes > 0);
  const title = mode === 'manual' ? 'Add appointment' : mode === 'edit' ? 'Edit appointment' : 'Review appointment';

  const setField = (field) => (event) => {
    const value = field === 'enabled' ? event.target.checked : event.target.value;
    setValues((current) => ({ ...current, [field]: value }));
    if (field === 'date' && value) setClarification('');
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    const appointmentPayload = {
      childId, title: values.title, provider: values.provider, date: values.date,
      time: values.time, timeZone, reminderMinutesBefore: reminderMinutes,
    };
    try {
      await configureAppointment({
        ...appointmentPayload,
        ...(appointment?.id ? { appointmentId: appointment.id } : {}),
        sourceRef: appointment?.sourceRef || null,
      });
      onClose(true);
    } catch {
      setSaving(false);
      setError('Appointment could not be saved. Check the details and try again.');
    }
  };

  const reminderHelp = values.enabled && values.date && values.time
    ? reminderMinutes === 1440 ? 'The care team will be reminded 24 hours before.'
      : reminderMinutes === 60 ? 'Because this is soon, the care team will be reminded one hour before.'
        : 'This appointment is less than one hour away, so a reminder will not be sent.'
    : 'Care Owners and Care Partners will receive the reminder.';

  const reviewFooter = <Stack direction="row" justifyContent="space-between" spacing={1}>
    <Box />
    <Stack direction="row" spacing={1}>
      <Button onClick={() => onClose(false)}>Close</Button>
      <Button form={FORM_ID} type="submit" variant="contained" disabled={saving || !values.title.trim() || !appointmentIsFuture}>{saving ? 'Saving…' : 'Save appointment'}</Button>
    </Stack>
  </Stack>;

  return <>
    <NaturalCaptureComposerShell
      open
      onClose={() => onClose(false)}
      title={title}
      subtitle={mode === 'review'
        ? 'Confirm the details before adding this to Schedule.'
        : 'Enter the appointment details.'}
      forceDrawer
      sheetSx={{ height: 'min(88dvh, 760px)', maxHeight: '88dvh' }}
      footer={reviewFooter}
    >
      {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}
      <Box id={FORM_ID} component="form" onSubmit={save}><Stack spacing={2}>
        {mode === 'review' && statement ? <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">Captured statement</Typography>
          <Typography>{statement}</Typography>
        </Box> : null}
        {clarification ? <Alert severity="info">{clarification}</Alert> : null}
        <TextField required label="Appointment title" value={values.title} onChange={setField('title')} inputProps={{ 'aria-label': 'Appointment title', maxLength: 120 }} />
        <TextField label="Provider (optional)" value={values.provider} onChange={setField('provider')} inputProps={{ 'aria-label': 'Provider (optional)', maxLength: 120 }} />
        <TextField required label="Date" type="date" value={values.date} onChange={setField('date')} InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Date', min: getZonedDayKey(new Date(), timeZone) }} />
        <TextField required label="Time" type="time" value={values.time} onChange={setField('time')} InputLabelProps={{ shrink: true }} inputProps={{ 'aria-label': 'Time' }} />
        <FormControlLabel control={<Switch checked={values.enabled && !reminderUnavailable} disabled={reminderUnavailable} onChange={setField('enabled')} />} label="Remind the care team" />
        <Typography variant="body2" color="text.secondary">{reminderHelp}</Typography>
      </Stack></Box>
    </NaturalCaptureComposerShell>
  </>;
};

export default AppointmentEditorSheet;
